import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';

import { Center, Loader, Text } from '@mantine/core';

import { Layout } from '@/components';
import { useAuth } from '@/contexts/AuthContext';
import {
  AuthCallbackPage,
  BouncePage,
  CasePage,
  ClientEnquiryDetailPage,
  ClientMonitoredSubjectPage,
  ClientMonitoringPage,
  ClientSettingsPage,
  CogsDashboardPage,
  ConfirmNotificationAddressPage,
  DataSourcesPage,
  EnquiryDetailPage,
  InboxPage,
  LoginPage,
  MonitoredSubjectsPage,
  MonitoringAlertsPage,
  NetworkPartnerEditPage,
  NetworkPartnersPage,
  PendingTriageDetailPage,
  PlaybookPage,
  PlaybooksPage,
  ProviderRatesPage,
  RegistryDocumentsPage,
  ReportConfigDocxMappingPage,
  ReportConfigEditPage,
  ReportConfigsPage,
  ReportConfigTemplateEditPage,
  ReportPage,
  ReportsPage,
  RequestDetailPage,
  RequestReportPage,
  RequestsPage,
  RiskProfileEditPage,
  RiskProfilesPage,
  ScopeComponentEditPage,
  ScopeComponentsPage,
  ScreeningBatchDetailPage,
  ScreeningBatchesPage,
  ScreeningBatchNewPage,
  UsageContractPage,
  UsageContractsPage,
  WorkbenchCasesPage,
} from '@/pages';
import { vnextApiClient } from '@/services/vnext-api';
import { CONFIRM_ADDRESS_PATH } from '@/utils/confirmationToken';
import { AdminRoute } from './AdminRoute';
import { ClientMonitoringRoute } from './ClientMonitoringRoute';
import { ProtectedRoute } from './ProtectedRoute';

/**
 * Phase 08 landing rewire: admins land on `/inbox`. Clients and any
 * other authenticated non-admin users keep the existing
 * `RequestReportPage` index. Network partners are redirected to
 * `/bounce` upstream in `ProtectedRoute`.
 */
function IndexRoute() {
  const { user } = useAuth();
  if (user?.isAdministrator) {
    return <Navigate to="/inbox" replace />;
  }
  return <RequestReportPage />;
}

function TriageRedirect() {
  const { requestId } = useParams<{ requestId: string }>();
  return <Navigate to={`/admin/triage/${requestId}`} replace />;
}

/** The standalone select-file page was retired — file selection lives in
 * the report page's Documents tab. Old inbox links land on the report. */
function SelectFileRedirect() {
  const { reportId } = useParams<{ reportId: string }>();
  return <Navigate to={`/reports/${reportId}`} replace />;
}

function SubjectReportRedirect() {
  const { subjectId } = useParams<{ subjectId: string }>();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!subjectId) {
      return;
    }
    let cancelled = false;
    vnextApiClient
      .getLatestReport(subjectId)
      .then((report) => {
        if (!cancelled) {
          navigate(`/reports/${report.id}`, { replace: true });
        }
      })
      .catch(() => {
        // The latest-report lookup is team-scoped server-side, so a cross-team
        // or stale subject id legitimately 404s. Surface a neutral message
        // rather than leaving the user on an infinite spinner (or throwing an
        // unhandled rejection).
        if (!cancelled) {
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [subjectId, navigate]);

  if (failed) {
    return (
      <Center h={300}>
        <Text c="dimmed">This report isn&apos;t available.</Text>
      </Center>
    );
  }

  return (
    <Center h={300}>
      <Loader />
    </Center>
  );
}

export function Router() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/bounce" element={<BouncePage />} />
      {/* Public: opened from an email by someone who is not a platform user. */}
      <Route path={CONFIRM_ADDRESS_PATH} element={<ConfirmNotificationAddressPage />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<Layout />}>
          <Route index element={<IndexRoute />} />
          <Route path="/request-report" element={<RequestReportPage />} />
          <Route path="/sources" element={<DataSourcesPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/reports/:reportId" element={<ReportPage />} />
          <Route path="/reports/:reportId/select-file" element={<SelectFileRedirect />} />
          {/* Client view of the pre-report pipeline (their team's report
              requests + per-request conversation). Admin triage stays
              under /admin/triage + /cases. */}
          <Route path="/requests" element={<RequestsPage />} />
          <Route path="/requests/enquiries/:enquiryId" element={<ClientEnquiryDetailPage />} />
          <Route path="/requests/:requestId" element={<RequestDetailPage />} />
          {/* Where monitoring alert and digest emails link to. Team-scoped
              and read-only; admins can open them too. Hidden unless
              VITE_CLIENT_MONITORING_ENABLED is true. */}
          <Route element={<ClientMonitoringRoute />}>
            <Route path="/monitoring" element={<ClientMonitoringPage />} />
            <Route
              path="/monitoring/subjects/:subjectId"
              element={<ClientMonitoredSubjectPage />}
            />
          </Route>
          {/* The Subjects browse page was retired — Reports is the only
              list surface. The per-subject report redirect survives for
              old links/bookmarks. */}
          <Route path="/subjects" element={<Navigate to="/reports" replace />} />
          <Route path="/subjects/overview" element={<Navigate to="/reports" replace />} />
          <Route path="/subjects/:subjectId/report" element={<SubjectReportRedirect />} />
          <Route element={<AdminRoute />}>
            <Route path="/inbox" element={<InboxPage />} />
            {/* The Upcoming tab retired with the Queue redesign — snoozed
                items live in the queue's collapsed Snoozed section. */}
            <Route path="/inbox/upcoming" element={<Navigate to="/inbox" replace />} />
            <Route path="/inbox/browse" element={<InboxPage initialTab="browse" />} />
            <Route path="/inbox/dashboard" element={<InboxPage initialTab="dashboard" />} />
            <Route path="/admin/client-settings" element={<ClientSettingsPage />} />
            {/* Client Intake became Client Settings when monitoring joined it. */}
            <Route
              path="/admin/client-intake"
              element={<Navigate to="/admin/client-settings" replace />}
            />
            <Route path="/admin/report-configs" element={<ReportConfigsPage />} />
            <Route path="/admin/report-configs/new" element={<ReportConfigEditPage />} />
            <Route path="/admin/report-configs/:configId" element={<ReportConfigEditPage />} />
            <Route
              path="/admin/report-configs/:configId/template"
              element={<ReportConfigTemplateEditPage />}
            />
            <Route
              path="/admin/report-configs/:configId/docx-mapping"
              element={<ReportConfigDocxMappingPage />}
            />
            <Route path="/admin/monitoring/alerts" element={<MonitoringAlertsPage />} />
            <Route path="/admin/monitoring/subjects" element={<MonitoredSubjectsPage />} />
            <Route path="/admin/registry-documents" element={<RegistryDocumentsPage />} />
            <Route path="/admin/network-partners" element={<NetworkPartnersPage />} />
            <Route path="/admin/network-partners/new" element={<NetworkPartnerEditPage />} />
            <Route path="/admin/network-partners/:npId" element={<NetworkPartnerEditPage />} />
            <Route path="/admin/scope-components" element={<ScopeComponentsPage />} />
            <Route path="/admin/scope-components/new" element={<ScopeComponentEditPage />} />
            <Route
              path="/admin/scope-components/:componentId"
              element={<ScopeComponentEditPage />}
            />
            <Route path="/admin/risk-profiles" element={<RiskProfilesPage />} />
            <Route path="/admin/provider-rates" element={<ProviderRatesPage />} />
            <Route path="/admin/usage" element={<UsageContractsPage />} />
            <Route path="/admin/cogs" element={<CogsDashboardPage />} />
            <Route path="/admin/usage/:contractId" element={<UsageContractPage />} />
            <Route path="/admin/risk-profiles/:profileId" element={<RiskProfileEditPage />} />
            <Route path="/workbench" element={<WorkbenchCasesPage />} />
            <Route path="/admin/playbooks" element={<PlaybooksPage />} />
            <Route path="/admin/playbooks/:key" element={<PlaybookPage />} />
            <Route path="/admin/screening-batches" element={<ScreeningBatchesPage />} />
            <Route path="/admin/screening-batches/new" element={<ScreeningBatchNewPage />} />
            <Route
              path="/admin/screening-batches/:batchId"
              element={<ScreeningBatchDetailPage />}
            />
            {/* Phase 03 retired PendingApprovalsPage; Phase 08 now owns the
                replacement. The `report_pending_approval` inbox kind
                surfaces what the old listing page used to render. */}
            <Route
              path="/admin/pending-approvals"
              element={<Navigate to="/inbox?kind=report_pending_approval" replace />}
            />
            {/* The triage + enquiry LIST pages retired into the Inbox
                (Browse tab, Cases mode) — detail routes live on. */}
            <Route path="/admin/triage" element={<Navigate to="/inbox?tab=browse" replace />} />
            {/* Phase 3: the case page — the codename family as a place.
                The detail routes below stay live as resolve-and-redirect
                shims (orphan fallback renders standalone). */}
            <Route path="/cases/:familyKey" element={<CasePage />} />
            <Route path="/admin/triage/:requestId" element={<PendingTriageDetailPage />} />
            {/* Phase 02: /admin/pending-triage renamed to /admin/triage */}
            <Route path="/admin/pending-triage" element={<Navigate to="/admin/triage" replace />} />
            <Route path="/admin/pending-triage/:requestId" element={<TriageRedirect />} />
            <Route path="/admin/enquiries" element={<Navigate to="/inbox?tab=browse" replace />} />
            <Route path="/admin/enquiries/:enquiryId" element={<EnquiryDetailPage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}
